import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Camera, Upload, Check, Loader2, ImageIcon, AlertTriangle, ShieldCheck } from "lucide-react";

const TYPES = ["OPG", "IOPA", "CBCT", "Clinical Photo", "Other"];

export default function MobileUpload() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [imageType, setImageType] = useState("OPG");
  const [uploads, setUploads] = useState([]); // {name, status}
  const camRef = useRef(null);
  const fileRef = useRef(null);

  const loadSession = async () => {
    try {
      const { data } = await axios.get(`${API}/upload-session/${token}`);
      setSession(data);
    } catch (e) {
      setError(e.response?.data?.detail || "This link is no longer valid.");
    }
  };

  useEffect(() => { loadSession(); }, [token]);

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    for (const f of list) {
      const entry = { name: f.name, status: "uploading" };
      setUploads((u) => [entry, ...u]);
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("image_type", imageType);
        await axios.post(`${API}/upload-session/${token}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setUploads((u) => u.map((x) => (x === entry ? { ...x, status: "done" } : x)));
      } catch {
        setUploads((u) => u.map((x) => (x === entry ? { ...x, status: "error" } : x)));
      }
    }
    loadSession();
  };

  const doneCount = uploads.filter((u) => u.status === "done").length;

  return (
    <div className="min-h-screen bg-[#111114] text-white px-5 py-8" style={{ background: "radial-gradient(60% 40% at 50% 0%, rgba(0,168,90,0.18), #111114 70%)" }}>
      <div className="mx-auto max-w-md">
        <div className="flex items-center gap-2 text-[#00A85A] font-extrabold text-lg tracking-tight">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#00A85A] text-white">C</span> CONTAHA
        </div>

        {error ? (
          <div className="mt-16 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-500/15"><AlertTriangle className="h-8 w-8 text-amber-400" /></div>
            <h1 className="mt-4 text-xl font-bold">Link unavailable</h1>
            <p className="mt-2 text-sm text-white/60">{error}</p>
          </div>
        ) : !session ? (
          <div className="mt-24 text-center"><Loader2 className="mx-auto h-7 w-7 animate-spin text-[#00A85A]" /></div>
        ) : (
          <>
            <div className="mt-8">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#00A85A]">Upload imaging</div>
              <h1 className="mt-1 text-2xl font-extrabold">{session.patient_name || "Consultation"}</h1>
              <p className="mt-1 text-sm text-white/55">Add X-rays or clinical photos from your phone. They attach to this consultation instantly.</p>
            </div>

            <div className="mt-6">
              <div className="text-xs font-medium text-white/60 mb-2">Image type</div>
              <div className="flex flex-wrap gap-2">
                {TYPES.map((t) => (
                  <button key={t} data-testid={`mtype-${t}`} onClick={() => setImageType(t)}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${imageType === t ? "bg-[#00A85A] text-white" : "bg-white/8 text-white/70"}`}>{t}</button>
                ))}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <button data-testid="mobile-take-photo" onClick={() => camRef.current?.click()} className="flex flex-col items-center gap-2 rounded-2xl border border-[#00A85A]/40 bg-[#00A85A]/10 py-7 active:scale-95 transition-transform">
                <Camera className="h-7 w-7 text-[#00A85A]" /><span className="text-sm font-bold">Take Photo</span>
              </button>
              <button data-testid="mobile-choose-file" onClick={() => fileRef.current?.click()} className="flex flex-col items-center gap-2 rounded-2xl border border-white/12 bg-white/5 py-7 active:scale-95 transition-transform">
                <Upload className="h-7 w-7 text-white/80" /><span className="text-sm font-bold">Choose File</span>
              </button>
            </div>
            <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleFiles(e.target.files)} />
            <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />

            {(uploads.length > 0) && (
              <div className="mt-7">
                <div className="flex items-center justify-between text-xs text-white/60 mb-2"><span>Uploads</span><span className="text-[#00A85A] font-semibold">{doneCount} added</span></div>
                <div className="space-y-2">
                  {uploads.map((u, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-xl bg-white/5 px-3 py-2.5">
                      <ImageIcon className="h-4 w-4 text-white/50 shrink-0" />
                      <span className="flex-1 truncate text-sm">{u.name}</span>
                      {u.status === "uploading" && <Loader2 className="h-4 w-4 animate-spin text-[#00A85A]" />}
                      {u.status === "done" && <Check className="h-4 w-4 text-[#00A85A]" />}
                      {u.status === "error" && <AlertTriangle className="h-4 w-4 text-amber-400" />}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10 flex items-center justify-center gap-1.5 text-[11px] text-white/40">
              <ShieldCheck className="h-3.5 w-3.5" /> Secure one-time upload link
            </div>
          </>
        )}
      </div>
    </div>
  );
}
